import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, Info, Award } from 'lucide-react';
import { Modal } from '../Modal/Modal';
import { listHouseholdPersons, type HouseholdPerson } from '../../lib/supabase';

interface HouseholdBadgeProps {
  email: string;
  memberCount: number;
  memberNames: string[];
  /** crm_id des "Gewinners" (= Primary). Wird im Modal hervorgehoben. */
  primaryCrmId?: string | null;
}

export function HouseholdBadge({ email, memberCount, memberNames, primaryCrmId }: HouseholdBadgeProps) {
  const [open, setOpen] = useState(false);
  const { data: persons, isLoading, isError, error } = useQuery({
    queryKey: ['household-persons', email],
    queryFn: () => listHouseholdPersons(email),
    enabled: open,
  });

  return (
    <>
      <button
        type="button"
        title={`Familien-Postfach: ${memberNames.join(', ')}`}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        style={{
          marginLeft: 6, padding: '0.05rem 0.4rem', fontSize: '0.65rem', borderRadius: 6,
          background: 'var(--info-light, #e6f4ff)', color: 'var(--info-dark, #0050b3)',
          fontWeight: 600, border: 'none', cursor: 'pointer',
        }}>
        Fam. {memberCount}
      </button>

      <Modal isOpen={open} onClose={() => setOpen(false)} title={`Familien-Postfach: ${email}`} size="md">
        <p style={{ marginTop: 0, color: 'var(--text-secondary)' }}>
          <Users size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />
          Diese E-Mail wird von <strong>{memberCount} Personen</strong> genutzt. Mails an diese
          Adresse gehen physisch nur einmal raus — alle Familienmitglieder lesen denselben
          Posteingang.
        </p>

        {isLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <div className="loading-spinner" />
          </div>
        )}
        {isError && (
          <div className="alert alert-danger">
            <Info size={16} />
            <div>Fehler beim Laden: {(error as Error).message}</div>
          </div>
        )}
        {persons && persons.length === 0 && (
          <div className="alert alert-info">
            <Info size={16} />
            <div>Keine Personen-Details verfuegbar (eventuell nur manueller Kontakt ohne CRM-Match).</div>
          </div>
        )}
        {persons && persons.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem' }}>
            {persons.map((p) => (
              <PersonCard key={p.membership_id} person={p} isPrimary={p.customer_id === primaryCrmId} />
            ))}
          </div>
        )}
      </Modal>
    </>
  );
}

function PersonCard({ person: p, isPrimary }: { person: HouseholdPerson; isPrimary?: boolean }) {
  const name = [p.first_name, p.last_name].filter(Boolean).join(' ') || '—';
  return (
    <div style={{
      border: `1px solid ${isPrimary ? 'var(--success-dark)' : 'var(--border-color)'}`,
      borderRadius: 'var(--radius-md)',
      padding: '0.75rem 1rem',
      background: isPrimary ? 'var(--success-light)' : 'var(--surface-secondary)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, gap: 8 }}>
        <strong style={{ fontSize: '0.9375rem' }}>{name}</strong>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {isPrimary && (
            <span className="status-badge"
              style={{ background: 'var(--success-dark)', color: 'white', fontSize: '0.65rem' }}>
              Primärkontakt
            </span>
          )}
          {p.is_trainer && (
            <span className="status-badge"
              style={{ background: 'var(--info-light)', color: 'var(--info-dark)', fontSize: '0.65rem' }}>
              <Award size={10} style={{ verticalAlign: '-1px', marginRight: 2 }} /> Trainer
            </span>
          )}
          {p.is_active_member ? (
            <span className="status-badge"
              style={{ background: 'var(--success-light)', color: 'var(--success-dark)', fontSize: '0.65rem' }}>
              aktiv
            </span>
          ) : (
            <span className="status-badge"
              style={{ background: 'var(--surface-secondary)', color: 'var(--text-secondary)', fontSize: '0.65rem' }}>
              {p.terminated ? 'gekündigt' : 'inaktiv'}
            </span>
          )}
        </div>
      </div>

      <div style={{
        fontSize: '0.8125rem', color: 'var(--text-secondary)',
        display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.25rem 1rem',
      }}>
        {p.organization && (<><span>Studio</span><span>{p.organization}</span></>)}
        {p.role &&         (<><span>Rolle</span><span>{p.role}</span></>)}
        {p.birth_date &&   (<><span>Geburtsdatum</span><span>{p.birth_date}</span></>)}
        {p.contract_start && (<><span>Vertragsbeginn</span><span>{p.contract_start}</span></>)}
        {p.contract_end &&   (<><span>Vertragsende</span><span>{p.contract_end}</span></>)}
      </div>
    </div>
  );
}
